import React from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { ArrowRight, CalendarDays, Printer, ReceiptText, UserRound } from 'lucide-react';
import DataTable from '@/components/shared/DataTable';
import SearchableSelect from '@/components/shared/SearchableSelect';
import PersianDatePickerFa from '@/components/shared/PersianDatePickerFa';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatRial } from '@/lib/formatMoney';
import type { LookupExpenseType } from '@/lib/api/accounting';
import type { ExpenseEntry } from '@/lib/mock/expenses';
import { beneficiaryTypeFa, employeeProfilePath, parseCounterpartyKey } from '@/pages/project-workspace/projectWorkspaceHelpers';

export interface ProjectWorkspaceBeneficiaryTabProps {
  projectId: number;
  projectLabel: string;
  beneficiaryKey: string;
  beneficiaryName: string;
  beneficiaryType: string;
  rows: ExpenseEntry[];
  columns: ColumnDef<ExpenseEntry, unknown>[];
  expenseTypes: LookupExpenseType[];
  filterTypeId: string;
  setFilterTypeId: (v: string) => void;
  filterDateFrom: string;
  filterDateTo: string;
  handleFilterDateFromChange: (v: string) => void;
  handleFilterDateToChange: (v: string) => void;
  filterDateError: string;
  clearFilters: () => void;
  onPrintCartex: () => void;
}

const ProjectWorkspaceBeneficiaryTab: React.FC<ProjectWorkspaceBeneficiaryTabProps> = ({
  projectId,
  projectLabel,
  beneficiaryKey,
  beneficiaryName,
  beneficiaryType,
  rows,
  columns,
  expenseTypes,
  filterTypeId,
  setFilterTypeId,
  filterDateFrom,
  filterDateTo,
  handleFilterDateFromChange,
  handleFilterDateToChange,
  filterDateError,
  clearFilters,
  onPrintCartex,
}) => {
  const totalAmount = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const cp = parseCounterpartyKey(beneficiaryKey);

  return (
    <div className="space-y-6 pt-2">
      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
        <Link to={`/projects/${projectId}/overview`} className="inline-flex items-center gap-1 text-emerald-700 hover:underline">
          <ArrowRight className="h-4 w-4" />
          بازگشت به خلاصهٔ مالی پروژه
        </Link>
        <span className="text-slate-300">/</span>
        <span className="line-clamp-1">{projectLabel}</span>
      </div>

      <Card className="overflow-hidden rounded-xl shadow-sm ring-1 ring-slate-200/60">
        <CardHeader className="border-b border-slate-200/80 bg-gradient-to-r from-violet-50 to-indigo-50 px-8 py-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-3">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-violet-700 shadow-sm ring-1 ring-violet-100">
                  <UserRound className="h-6 w-6" />
                </span>
                <div className="min-w-0">
                  <CardTitle className="text-xl font-semibold text-slate-900">{beneficiaryName}</CardTitle>
                  <CardDescription className="mt-1 text-base text-slate-600">
                    {beneficiaryTypeFa(beneficiaryType)} · واریزها و هزینه‌های ثبت‌شده در حسابداری برای همین پروژه
                  </CardDescription>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-slate-700 ring-1 ring-slate-200">
                  <ReceiptText className="h-4 w-4 text-emerald-600" />
                  {rows.length} ردیف سند
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 font-mono text-rose-700 ring-1 ring-rose-100">
                  جمع: {formatRial(totalAmount)}
                </span>
                {rows[0]?.dateFa ? (
                  <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-slate-700 ring-1 ring-slate-200">
                    <CalendarDays className="h-4 w-4 text-sky-600" />
                    آخرین: <span dir="ltr">{rows[0].dateFa}</span>
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {cp?.type === 'employee' ? (
                <Button asChild variant="secondary" size="sm" className="inline-flex gap-2">
                  <Link to={employeeProfilePath(cp.id)}>
                    <UserRound className="h-4 w-4" />
                    پروفایل کامل کارمند
                  </Link>
                </Button>
              ) : null}
              <Button type="button" variant="outline" size="sm" className="inline-flex gap-2" onClick={onPrintCartex}>
                <Printer className="h-4 w-4" />
                چاپ کارتکس این شخص
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 px-4 py-8 sm:px-8">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <DataTable
              columns={columns}
              data={rows}
              emptyMessage="برای این شخص در این پروژه سندی با فیلتر فعلی ثبت نشده."
              toolbar={
                <div className="flex flex-wrap gap-4">
                  <div className="min-w-[12rem]">
                    <SearchableSelect
                      value={filterTypeId}
                      onChange={setFilterTypeId}
                      options={[
                        { value: '', label: 'همهٔ انواع هزینه' },
                        ...expenseTypes.map((t) => ({ value: String(t.id), label: t.title })),
                      ]}
                      placeholder="نوع هزینه…"
                    />
                  </div>
                  <div className="min-w-[10rem]">
                    <PersianDatePickerFa value={filterDateFrom} onChange={handleFilterDateFromChange} placeholder="از تاریخ…" />
                  </div>
                  <div className="min-w-[10rem]">
                    <PersianDatePickerFa value={filterDateTo} onChange={handleFilterDateToChange} placeholder="تا تاریخ…" />
                  </div>
                  <Button type="button" variant="outline" className="h-10 shrink-0" onClick={clearFilters}>
                    پاک کردن فیلترها
                  </Button>
                  {filterDateError ? (
                    <div className="w-full rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                      {filterDateError}
                    </div>
                  ) : null}
                </div>
              }
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectWorkspaceBeneficiaryTab;
